<template>
  <div class="lead-details">
    <BasePaneTray
      context="leadHistoryContext"
      :isOpen="isOpen"
    >
      <template #headerPlus>
        <div class="lead-details__header">
          <h2
            v-if="heading"
            class="lead-details__header__heading"
          >
            {{ heading }}
          </h2>

          <h4
            v-if="subheading"
            class="lead-details__header__subheading"
            v-html="subheading"
          />
        </div>
      </template>

      <template #body>
        <BasePaneCard
          :isLoading="isLoading"
          unboxed
          class="lead-details__body"
        >
          <div
            v-if="!dataset?.error && !isLoading"
            class="lead-details__groups"
          >
            <div
              v-for="group in groups"
              :key="group.name"
              class="lead-details__group"
            >
              <h4 class="lead-details__group__heading">
                {{ group.name }}
              </h4>

              <div
                v-for="attribute in group.attributes"
                :key="attribute.key"
                class="lead-details__group__attribute"
              >
                <div
                  v-if="attribute.key === 'conversation_summary' && dataset.chatMetrics"
                  class="lead-details__group__attribute__chat-metrics"
                >
                  <p>
                    {{ totalMessages }}
                  </p>

                  <p>
                    {{ attendanceTime }}
                  </p>
                </div>

                <span
                  v-if="attribute.key !== 'conversation_summary'"
                  class="lead-details__group__attribute__label"
                >
                  {{ attribute.label }}:
                </span>

                <span class="lead-details__group__attribute__value">
                  {{ attribute.value }}
                </span>
              </div>
            </div>
          </div>

          <BaseEmptyState v-else>
            <SvgIconRobot/>

            <h3>Ops! Algo deu errado...</h3>
            <p>Por favor tente novamente.</p>
          </BaseEmptyState>
        </BasePaneCard>
      </template>
    </BasePaneTray>
  </div>
</template>

<script>
import {
  dateFilter,
  phoneFilter,
  cpfFilter,
  currencyFilter,
  numberFilter,
  regionFilter,
  capitalizeFilter,
  accountCardNumberFilter
} from '@/utils/filters'
import BasePaneTray from '@/components/base/BasePaneTray'
import BasePaneCard from '@/components/base/BasePaneCard'
import BaseEmptyState from '@/components/base/BaseEmptyState'
import { mapState, mapGetters, mapActions } from 'vuex'
import axios from 'axios'
import dayjs from 'dayjs'
import durationPlugin from 'dayjs/plugin/duration'
import humanizeDuration from 'humanize-duration'
import { platform } from 'chart.js'

dayjs.extend(durationPlugin)

export default {
  name: 'CommonLeadDetails',

  components: {
    BasePaneTray,
    BasePaneCard,
    BaseEmptyState
  },

  filters: {
    date: dateFilter,
    phone: phoneFilter,
    cpf: cpfFilter,
    currency: currencyFilter,
    number: numberFilter,
    region: regionFilter,
    accountCardNumber: accountCardNumberFilter
  },

  props: {
    isOpen: Boolean,
    heading: String,
    subheading: String,
    leadId: Number
  },

  data () {
    return {
      isLoading: false,
      dataset: null,
      attributesGroups: [
        {
          name: 'Perfil',
          attributes: [
            'email',
            'home_phone',
            'zipcode',
            'address',
            'state',
            'regional'
          ]
        },
        {
          name: 'Produto',
          attributes: [
            'order_status',
            'product',
            'details',
            'payment',
            'value',
            'item_quantity',
            'latest_status',
            'lead_strategy'
          ]
        },
        {
          name: 'Resumo da conversa',
          attributes: [
            'conversation_summary'
          ]
        },
        {
          name: 'Atendimento',
          attributes: [
            'lead_consultant',
            'fup_consultor',
            'date_sale',
            'invoice_date',
            'reason_abandonment',
          ]
        },
        {
          name: 'Dados adicionais',
          attributesIgnore: []
        }
      ]
    }
  },

  computed: {
    ...mapState('base', [
      'mainApi',
      'platformAttributes',
      'platforms'
    ]),

    ...mapGetters('auth', [
      'platformId'
    ]),

    attributesLabels () {
      const labels = {}

      this.platformAttributes.forEach(attribute => {
        const { name, label } = attribute

        labels[name] = label
      })

      return labels
    },

    attributes () {
      if (!this.dataset) {
        return []
      }

      return Object.entries(this.dataset.leadDetails.content)
        .map(([key, value]) => {
          switch (key) {
            case 'email':
              value = value.toLowerCase()
              break

            case 'phone':
            case 'home_phone':
              value = phoneFilter(value)
              break

            case 'cpf':
              value = cpfFilter(value)
              break

            case 'value':
              value = currencyFilter(value)
              break

            case 'item_quantity':
              value = numberFilter(value)
              break

            case 'date_sale':
            case 'invoice_date':
              value = dateFilter(value)
              break

            case 'regional':
              value = regionFilter(value)
              break

            case 'details':
              value = accountCardNumberFilter(value)
              break

            case 'account_card_number':
              value = accountCardNumberFilter(value, true)
          }

          return {
            key,
            label: this.attributesLabels[key],
            value
          }
        })
        .filter(attribute => !this.platformAttributesIgnore.includes(attribute.key))
    },

    groups () {
      let attributes = this.attributes

      return this.attributesGroups
        .map(group => {
          const attributesGroupsLast = this.attributesGroups[this.attributesGroups.length -1]
          let groupAttributes

          if (group.name === attributesGroupsLast.name) {
            groupAttributes = attributes.filter(({ key }) => !group.attributesIgnore.includes(key))
            if (this.$auth.user().profile === 'platform') {
              groupAttributes = groupAttributes.filter(({ key }) => key !== 'note_internal')
            }
          } else {
            groupAttributes = group.attributes
              .filter(attribute => attributes.some(({ key }) => key === attribute))
              .map(attribute => {
                const element = attributes.find(({ key }) => key === attribute)

                attributes = attributes.filter(({ key }) => key !== attribute)

                return element
              })

          }

          return {
            name: group.name,
            attributes: groupAttributes
          }
        })
        .filter(group => group.attributes.length > 0)
    },

    totalMessages () {
      const { chatMetrics: metrics } = this.dataset

      if (!metrics) {
        return ''
      }

      return `Total de mensagens trocadas: ${numberFilter(metrics.total_messages)}`
    },

    attendanceTime () {
      const { chatMetrics: metrics } = this.dataset

      if (!metrics) {
        return ''
      }

      const [days, time] = metrics.date_difference.split(' ')
      const [hours, minutes, seconds] = time.split(':')

      const duration = dayjs.duration({
        seconds: Number(seconds),
        minutes: Number(minutes),
        hours: Number(hours),
        days: Number(days)
      })

      const milliseconds = duration.asMilliseconds()

      const humanized = humanizeDuration(milliseconds, {
        language: 'pt',
        conjunction: ' e ',
        serialComma: false,
      })

      return `${capitalizeFilter(humanized)} de atendimento`
    },

    platformAttributesIgnore () {
      return this.platforms
        .filter(platform => platform.id === this.platformId)
        .map(platform => platform.leadDetails.attributesIgnore)
        .flat()
    }
  },

  watch: {
    leadId () {
      this.fetchData()
    }
  },

  created () {
    this.fetchPlatformAttributes()
  },

  methods: {
    ...mapActions('base', ['fetchPlatformAttributes']),

    async fetchData () {
      if (!this.leadId) {
        return
      }

      this.isLoading = true

      try {
        const url = `${this.mainApi}lead/${this.leadId}`
        const response = await axios.get(url)

        if (response?.data?.content?.tabulation === 'Tabulação APP') delete response.data.content.tabulation

        this.dataset = {
          leadDetails: response.data
        }
      } catch (error) {
        this.dataset = { error }

        throw error
      } finally {
        if (this.dataset.leadDetails?.content.conversation_summary) {
          this.fetchChatMetricsData()
        } else {
          this.isLoading = false
        }
      }
    },

    async fetchChatMetricsData () {
      if (!this.leadId) {
        return
      }

      this.isLoading = true

      try {
        const url = `${this.mainApi}integration/app/get-message/${this.leadId}?metrics=true`
        const response = await axios.get(url)

        Object.assign(this.dataset, {
          chatMetrics: response.data.metrics
        })
      } catch (error) {
        throw error
      } finally {
        this.isLoading = false
      }
    },
  }
}
</script>

<style lang="scss" scoped>
.lead-details {
  $border: 2px solid nth($colors-custom, 4);

  &__header {
    &__heading {
      font: {
        weight: 700;
        size: 16px;
      }
      line-height: 120%;
    }

    &__subheading {
      margin-top: 5px;
      font-size: 12px;
      line-height: 140%;

      /deep/ {
        div {
          &:not(:first-child) {
            margin-top: 10px;
            font-weight: 600;
          }
        }
      }
    }
  }

  &__body {
    height: 100%;
    overflow: auto;
    padding-right: 15px;
  }

  &__group {
    &:not(:first-child) {
      padding-top: 20px;
      margin-top: 20px;
      border-top: 2px solid nth($colors-custom, 4);
    }

    &__heading {
      font: {
        weight: 700;
        size: 16px;
      }
    }

    &__attribute {
      margin-top: 10px;
      font-size: 12px;

      &__label {
        font-weight: 600;
      }

      &__chat-metrics {
        padding-bottom: 10px;
        color: nth($colors-custom, 15);
        font-size: 12px;
        line-height: 140%;
      }
    }
  }
}
</style>
